import React, { useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { projects, personalInfo } from "../../data/portfolio";
import Reveal from "../motion/Reveal";
import SplitLines from "../motion/SplitLines";
import AxisReveal from "../motion/AxisReveal";
import Decode from "../motion/Decode";
import Ruler from "../motion/Ruler";
import MaskMedia from "../motion/MaskMedia";
import ProjectMedia from "../ui/ProjectMedia";
import Magnetic from "../ui/Magnetic";
import Icon from "../ui/Icon";

/**
 * Specimen sheet. Everything here comes from the project record — overview,
 * stack, outcome readouts, links. Nothing is generated to fill space.
 */
const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const scope = useRef(null);

  const index = projects.findIndex((p) => String(p.id) === String(id));
  const project = index >= 0 ? projects[index] : null;
  const next = projects[(index + 1) % projects.length];

  // Route changes must start at the top, with Lenis in sync.
  useEffect(() => {
    window.scrollTo(0, 0);
    window.lenis?.scrollTo(0, { immediate: true });
  }, [id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") navigate("/#work");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navigate]);

  if (!project) {
    return (
      <section className="chapter flex min-h-[100svh] flex-col justify-center px-margin">
        <span className="t-label">Error 404</span>
        <h1 className="t-h2 mt-4 text-fg">No specimen with that number.</h1>
        <Link to="/" className="control mt-8">
          <Icon name="arrowLeft" />
          Back home
        </Link>
      </section>
    );
  }

  const results = Object.entries(project.results || {});
  const links = [
    project.demoUrl && { label: "Live demo", href: project.demoUrl },
    project.githubUrl && { label: "Source", href: project.githubUrl },
  ].filter(Boolean);

  return (
    <article ref={scope} className="chapter relative">
      {/* Header */}
      <header className="page hair-b flex items-center justify-between py-4">
        <Magnetic padding={14}>
          <Link
            to="/#work"
            className="t-label-strong flex min-h-[44px] items-center gap-2 py-3"
            data-cursor-label="Back"
          >
            <Icon name="arrowLeft" />
            All work
          </Link>
        </Magnetic>
        <span className="t-label">
          <span className="text-flare">{project.index}</span> — {project.status}
        </span>
      </header>

      {/* Title block */}
      <div className="page pt-14 md:pt-20">
        <Decode as="span" className="t-label" text={project.category} />
        <AxisReveal as="h1" className="t-display mt-4 text-fg" wdth={108} wght={700} fromWdth={68}>
          {project.title}
        </AxisReveal>
        <SplitLines as="p" className="t-lead mt-6 max-w-[54ch] text-dim" stagger={0.07}>
          {project.description}
        </SplitLines>
      </div>

      {/* Media */}
      <div className="page mt-12 md:mt-16">
        <MaskMedia className="media-frame aspect-[16/9]" start="top 88%">
          <ProjectMedia
            project={project.videoFull ? { ...project, video: project.videoFull } : project}
            sizes="100vw"
            eager
          />
        </MaskMedia>
      </div>

      {/* Specification + overview */}
      <div className="page mt-16 grid-12 gap-y-14 md:mt-24">
        <aside className="col-span-12 md:col-span-4" aria-label="Specification">
          <Reveal stagger={0.06} className="flex flex-col gap-6">
            <div data-reveal-item className="hair-t pt-3">
              <span className="t-label block">Stack</span>
              <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
                {project.techStack.map((tech) => (
                  <li key={tech} className="text-sm text-fg">
                    {tech}
                  </li>
                ))}
              </ul>
            </div>

            <div data-reveal-item className="hair-t pt-3">
              <span className="t-label block">Status</span>
              <span className="mt-2 block text-sm text-fg">{project.status}</span>
            </div>

            {links.length > 0 && (
              <div data-reveal-item className="hair-t flex flex-col gap-3 pt-3">
                <span className="t-label">Links</span>
                {links.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="control"
                    data-cursor-label="Open"
                  >
                    {link.label}
                    <Icon name="arrowOut" />
                  </a>
                ))}
              </div>
            )}
          </Reveal>
        </aside>

        <div className="col-span-12 md:col-span-7 md:col-start-6">
          <Reveal>
            <span className="t-label">Overview</span>
          </Reveal>
          <Ruler ticks={12} className="mt-3" />
          <SplitLines as="p" className="t-body mt-5" stagger={0.06}>
            {project.longDescription}
          </SplitLines>

          {results.length > 0 && (
            <>
              <Reveal className="hair-t mt-12 pt-4">
                <span className="t-label">Outcome</span>
              </Reveal>
              <Reveal stagger={0.07} className="mt-6 grid gap-x-8 gap-y-6 sm:grid-cols-3">
                {results.map(([key, value]) => (
                  <div key={key} data-reveal-item>
                    <span className="t-label block">[ {key} ]</span>
                    <span className="mt-1.5 block text-sm text-fg">{value}</span>
                  </div>
                ))}
              </Reveal>
            </>
          )}
        </div>
      </div>

      {/* Next specimen */}
      <div className="page mt-24 md:mt-32">
        <Link
          to={`/project/${next.id}`}
          className="hair-t group flex flex-col gap-3 py-10 md:flex-row md:items-baseline md:justify-between"
          data-cursor-label="Next"
        >
          <span className="t-label">Next — {next.index}</span>
          <span className="t-h2 text-fg transition-colors duration-300 group-hover:text-flare">
            {next.title}
          </span>
        </Link>
      </div>

      <footer className="page hair-t flex flex-wrap items-center justify-between gap-4 py-6">
        <span className="t-label">
          {personalInfo.fullName} — {personalInfo.location}
        </span>
        <Link to="/" className="t-label-strong link-wipe flex items-center gap-2">
          <Icon name="arrowLeft" />
          All work
        </Link>
      </footer>
    </article>
  );
};

export default ProjectDetail;
